import styled from '@emotion/styled';
import {
  BetweenHorizonalStart,
  Bot,
  Bug,
  FileSymlink,
  ImportIcon,
  TableOfContents,
  TrendingUpDown,
  UploadCloudIcon,
  CircleDot,
  Wand2,
  Wrench,
} from 'lucide-react';
import { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Body = styled.div`
  width: 100%;
  height: 100%;
  min-height: 100vh;
`;

const Header = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const Content = styled.div`
  width: 100%;
  display: flex;
`;

const Sidebar = styled.div`
  width: 14rem;
  position: fixed;
  left: 0;
  top: 5rem;
  bottom: 0;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  padding: 2rem 0;
  background: #fff;
  border-radius: 0 1rem 0 0;
`;

const Main = styled.main`
  margin-bottom: 2rem;
  padding: 2rem 2rem 2rem 16rem;
  width: 100%;
`;

const Footer = styled.footer`
  width: 100%;
  margin-top: auto;
  font-size: 0.725rem;
  text-align: center;
  padding: 1rem 0;
  color: #14A4BCFF;
  opacity: .5;
`;

interface LayoutProps {
  children: ReactNode;
}

export const Layout: React.FC<LayoutProps> = (props) => {
  const { children } = props;
  const location = useLocation();

  return (
    <Body>
      <Header>
        <Link to={'/'}>
          <img src={'/ovd.svg'} alt={'AQE OVD'} className={'w-[220px] h-[34px]'} />
        </Link>
      </Header>
      <Content>
        <Sidebar>
          <div className={'w-full flex justify-center mb-5'}>
            <img src={'/AQEblack.png'} alt={'AQE'} className={'w-[100px]'} />
          </div>
          <Link
            to={'/'}
            className={`w-full flex items-center gap-2 p-2 transition-all hover:bg-[#14A4BCFF] hover:text-white ${location.pathname === '/' ? 'bg-[#14A4BCFF] text-white' : 'text-black'}`}
          >
            <FileSymlink size={24} />
            Validate
          </Link>
          <Link
            to={'/upload'}
            className={`w-full flex items-center gap-2 p-2 transition-all hover:bg-[#14A4BCFF] hover:text-white ${location.pathname === '/upload' ? 'bg-[#14A4BCFF] text-white' : 'text-black'}`}
          >
            <UploadCloudIcon size={24} />
            Upload
          </Link>
          <Link
            to={'/predictions'}
            className={`w-full flex items-center gap-2 p-2 transition-all hover:bg-[#14A4BCFF] hover:text-white ${location.pathname === '/predictions' ? 'bg-[#14A4BCFF] text-white' : 'text-black'}`}
          >
            <TrendingUpDown size={24} />
            Predictions
          </Link>
          <Link
            to={'/contexts'}
            className={`w-full flex items-center gap-2 p-2 transition-all hover:bg-[#14A4BCFF] hover:text-white ${location.pathname === '/contexts' ? 'bg-[#14A4BCFF] text-white' : 'text-black'}`}
          >
            <TableOfContents size={24} />
            Contexts
          </Link>
          <Link
            to={'/paragraphs'}
            className={`w-full flex items-center gap-2 p-2 transition-all hover:bg-[#14A4BCFF] hover:text-white ${location.pathname === '/paragraphs' ? 'bg-[#14A4BCFF] text-white' : 'text-black'}`}
          >
            <BetweenHorizonalStart size={24} />
            Paragraphs
          </Link>
          <Link
            to={'/reports'}
            className={`w-full flex items-center gap-2 p-2 transition-all hover:bg-[#14A4BCFF] hover:text-white ${location.pathname === '/reports' ? 'bg-[#14A4BCFF] text-white' : 'text-black'}`}
          >
            <ImportIcon size={24} />
            Reports
          </Link>
          <Link
            to={'/bubble'}
            className={`w-full flex items-center gap-2 p-2 transition-all hover:bg-[#14A4BCFF] hover:text-white ${location.pathname === '/bubble' ? 'bg-[#14A4BCFF] text-white' : 'text-black'}`}
          >
            <CircleDot size={24} />
            Bubble
          </Link>
          <Link
            to={'/metrics'}
            className={`w-full flex items-center gap-2 p-2 transition-all hover:bg-[#14A4BCFF] hover:text-white ${location.pathname === '/metrics' ? 'bg-[#14A4BCFF] text-white' : 'text-black'}`}
          >
            <Wrench size={24} />
            Metrics
          </Link>
          <Link
            to={'/api-tokens'}
            className={`w-full flex items-center gap-2 p-2 transition-all hover:bg-[#14A4BCFF] hover:text-white ${location.pathname === '/api-tokens' ? 'bg-[#14A4BCFF] text-white' : 'text-black'}`}
          >
            <Bot size={24} />
            API
          </Link>
          <Link
            to={'/prompt-engineering'}
            className={`w-full flex items-center gap-2 p-2 transition-all hover:bg-[#14A4BCFF] hover:text-white ${location.pathname === '/prompt-engineering' ? 'bg-[#14A4BCFF] text-white' : 'text-black'}`}
          >
            <Wand2 size={24} />
            Prompt Engineering
          </Link>
          <Link
            to={'/debug-prompt'}
            className={`w-full flex items-center gap-2 p-2 transition-all hover:bg-[#14A4BCFF] hover:text-white ${location.pathname === '/debug-prompt' ? 'bg-[#14A4BCFF] text-white' : 'text-black'}`}
          >
            <Bug size={24} />
            Debug Prompt
          </Link>
          <Footer>App version: {import.meta.env.VITE_APP_VERSION}</Footer>
        </Sidebar>
        <Main>
          <div className={'container'}>{children}</div>
        </Main>
      </Content>
    </Body>
  );
};
